import React from "react";
import styled from "styled-components";
import PlatFormArtistCard from "./PlatFormArtistCard";
import { PlatformTrackInfoProps } from "../types";
import {FormattedMessage} from "react-intl";

const truncateText = (value: any, max = 20) => {
    const s = String(value ?? "");
    if (s.length <= max) return s;
    return `${s.slice(0, max)}...`;
};

const PlatformTrackInfo: React.FC<PlatformTrackInfoProps> = ({
    title,
    version,
    language,
    releases,
    audio,
    id,
    isrc,
    artists,
    contributors: contributorsProp,
    genres: genresProp,
}) => {
    const releaseData = releases?.releases?.data ?? {};
    const contributorsRaw = contributorsProp ?? releaseData.contributors ?? [];
    const genres = genresProp ?? releaseData?.genres ?? [];

    const normalizeRoleNames = (c: any): string[] => {
        const rolesRaw =
            // Сначала трековые роли (role / roles), затем глобальные роли контрибутора.
            (Array.isArray(c?.roles) && c.roles.length ? c.roles :
              c?.role ? [c.role] :
                c?.contributor?.roles ??
            c?.contributor_roles ??
            c?.roles_list ??
            c?.role_list ??
                []);
        if (!Array.isArray(rolesRaw)) return [];
        return rolesRaw
            .map((r: any) => (typeof r === "string" ? r : r?.name))
            .filter((v: any): v is string => Boolean(v));
    };

    const normalizeInstrumentNames = (c: any): string[] => {
        const instrumentsRaw =
            // Только трековые инструменты (instrument / instruments) этого трека.
            // Глобальные instruments контрибутора игнорируем.
            (Array.isArray(c?.instruments) && c.instruments.length
              ? c.instruments
              : c?.instrument
                ? [c.instrument]
                : []);
        if (!Array.isArray(instrumentsRaw)) return [];
        return instrumentsRaw
            .map((r: any) => (typeof r === "string" ? r : r?.name))
            .filter((v: any): v is string => Boolean(v));
    };

    const normalizeContributorLabels = (c: any): string[] => {
        const roleNames = normalizeRoleNames(c);
        const instrumentNames = normalizeInstrumentNames(c);
        return Array.from(new Set([...roleNames, ...instrumentNames]));
    };

    // Merge duplicate contributors (same base id) into a single display entry
    const mergeContributors = (list: any[]): any[] => {
        const byId = new Map<string, any>();
        const mergeArray = (target: any, src: any[], dstPath: "roles" | "instruments") => {
            if (!Array.isArray(src)) return;
            if (!Array.isArray(target[dstPath])) target[dstPath] = [];
            src.forEach((val: any) => {
                const k = String(val?.id ?? val?.name ?? "");
                if (!k) return;
                if (target[dstPath].some((x: any) => String(x?.id ?? x?.name ?? "") === k)) return;
                target[dstPath].push(val);
            });
        };

        list.forEach((item: any) => {
            if (!item) return;
            const base = item?.contributor ?? item;
            const idRaw = base?.id ?? item?.id;
            if (idRaw == null) return;
            const key = String(idRaw);
            const name = base?.name ?? base?.title ?? "";

            if (!byId.has(key)) {
                const entry: any = {
                    ...item,
                    contributor: base,
                    id: idRaw,
                    name,
                    roles: [],
                    instruments: [],
                };
                mergeArray(entry, item?.roles || (item?.role ? [item.role] : []) || base?.roles || [], "roles");
                mergeArray(entry, item?.instruments || (item?.instrument ? [item.instrument] : []) || base?.instruments || [], "instruments");
                byId.set(key, entry);
            } else {
                const existing = byId.get(key)!;
                mergeArray(existing, item?.roles || (item?.role ? [item.role] : []) || base?.roles || [], "roles");
                mergeArray(existing, item?.instruments || (item?.instrument ? [item.instrument] : []) || base?.instruments || [], "instruments");
            }
        });
        return Array.from(byId.values());
    };

    const contributors = Array.isArray(contributorsRaw) ? mergeContributors(contributorsRaw) : [];

    return (
        <Wrapper>
            <Section>
                <SubTitle>
                    <FormattedMessage
                      id="ReleaseDetails.TrackInfo.Artists"
                      defaultMessage="Artists"
                    />
                </SubTitle>
                <SubTitleText>
                    {artists?.map((artist, index) => (
                        <span key={artist?.id ?? index} title={artist.name}>
                                  {artist?.name}
                          {artists.length - 1 !== index ? ", " : ""}
                        </span>
                    ))}
                </SubTitleText>
                <ArtistsGrid>
                    {(() => {
                        const list = Array.isArray(artists) ? artists : [];
                        const primaryCandidates = list.filter((a: any) =>
                            a?.is_primary === true ||
                            a?.is_primary === 1 ||
                            a?.role === "MAIN" ||
                            a?.status === true
                        );
                        const primaryId = primaryCandidates[0]?.id ?? list[0]?.id;

                        return list.map((artist: any) => (
                            <PlatFormArtistCard
                                key={artist?.id}
                                name={artist.name}
                                isPrimary={primaryId != null ? String(artist?.id) === String(primaryId) : false}
                                onTogglePrimary={() => console.log(`Toggled primary for artist ${artist.id}`)}
                                onDelete={() => console.log(`Deleted artist ${artist.id}`)}
                            />
                        ));
                    })()}
                </ArtistsGrid>
            </Section>

            <InfoGrid>
                <InfoBlockTitle>
                    <FormattedMessage
                      id="ReleaseDetails.TrackInfo.TrackDetails"
                      defaultMessage="Track details"
                    />
                </InfoBlockTitle>
                <InfoBlockContent>
                    <InfoBlock>
                        <InfoBlockText>
                            <FormattedMessage
                              id="ReleaseDetails.TrackInfo.TrackTitle"
                              defaultMessage="Track title"
                            />
                        </InfoBlockText>
                        <Text title={String(title ?? "")}>{truncateText(title, 20)}</Text>
                    </InfoBlock>
                    <InfoBlock>
                        <InfoBlockText>
                            <FormattedMessage
                              id="ReleaseDetails.TrackInfo.TrackVersion"
                              defaultMessage="Track version"
                            />
                        </InfoBlockText>
                        <Text>{version}</Text>
                    </InfoBlock>
                    <InfoBlock>
                        <InfoBlockText>
                            <FormattedMessage
                              id="ReleaseDetails.TrackInfo.TrackLanguage"
                              defaultMessage="Track language"
                            />
                        </InfoBlockText>
                        <Text>{language}</Text>
                    </InfoBlock>
                </InfoBlockContent>
            </InfoGrid>

            <Section>
                <SubTitle>
                    <FormattedMessage
                      id="ReleaseDetails.TrackInfo.Contributors"
                      defaultMessage="Contributors"
                    />
                </SubTitle>
                <ContributorsWrapper>
                    {Array.isArray(contributors) && contributors.length > 0 ? (
                        contributors.map((contributor: any, index: number) => {
                            const name =
                              contributor?.name ??
                              contributor?.contributor?.name ??
                              "";
                            return (
                              <SubTitleText key={contributor?.id ?? index}>
                                {name}
                                {contributors.length - 1 !== index ? ", " : ""}
                              </SubTitleText>
                            );
                        })
                    ) : (
                        <SubTitleText>—</SubTitleText>
                    )}
                    <ContributorsContainer>
                        {Array.isArray(contributors) && contributors?.map((contributor: any, index: number) => {
                            const labels = normalizeContributorLabels(contributor);
                            const name =
                              contributor?.name ??
                              contributor?.contributor?.name ??
                              "";
                            return (
                                <ContributorCard key={contributor.id ?? index}>
                                    <ContributorCardBox>
                                        {name}{contributors.length - 1 !== index ? ", " : ''}
                                    </ContributorCardBox>
                                    <ContributorCardInfo>
                                        {labels.length ? labels.join(", ") : "—"}
                                    </ContributorCardInfo>
                                </ContributorCard>
                            );
                        })}
                    </ContributorsContainer>
                </ContributorsWrapper>
            </Section>

            <Section>
                <InfoGrid>
                    <InfoBlockTitle>
                        <FormattedMessage
                          id="ReleaseDetails.TrackInfo.Genres"
                          defaultMessage="Genres"
                        />
                    </InfoBlockTitle>
                    <InfoBlockContent>
                        <InfoBlock>
                            <InfoBlockText>
                                <FormattedMessage
                                  id="ReleaseDetails.TrackInfo.PrimaryGenre"
                                  defaultMessage="PrimaryGenre"
                                />
                            </InfoBlockText>
                            <Text>
                                {(genres ?? [])
                                  .filter((genre: any) => genre?.is_primary === true || genre?.is_primary === 1 || genre?.is_primary === "1")
                                  .map((genre: any, index: number, arr: any[]) => (
                                    <span key={genre?.id ?? index}>{genre.name}{arr.length - 1 !== index ? ", " : ''}</span>
                                  ))}
                            </Text>
                        </InfoBlock>
                        <InfoBlock>
                            <InfoBlockText>
                                <FormattedMessage
                                  id="ReleaseDetails.TrackInfo.AdditionalGenres"
                                  defaultMessage="Additional Genres"
                                />
                            </InfoBlockText>
                            <Text>
                                {(genres ?? [])
                                  .filter((genre: any) => !(genre?.is_primary === true || genre?.is_primary === 1 || genre?.is_primary === "1"))
                                  .map((genre: any, index: number, arr: any[]) => (
                                    <span key={genre?.id ?? index}>{genre.name}{arr.length - 1 !== index ? ", " : ''}</span>
                                  ))}
                            </Text>
                        </InfoBlock>
                    </InfoBlockContent>
                </InfoGrid>
            </Section>

            <Section>
                <InfoGrid>
                    <InfoBlockTitle>
                        <FormattedMessage
                          id="ReleaseDetails.TrackInfo.ISRC"
                          defaultMessage="ISRC"
                        />
                    </InfoBlockTitle>
                    <InfoBlockContent>
                        <InfoBlock>
                            <InfoBlockText>
                                <FormattedMessage
                                  id="ReleaseDetails.TrackInfo.MyISRC"
                                  defaultMessage="My own ISRC"
                                />
                            </InfoBlockText>
                            <Text>{isrc}</Text>
                        </InfoBlock>
                    </InfoBlockContent>
                </InfoGrid>
            </Section>
        </Wrapper>
    );
};


const Wrapper = styled.section`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 30px;
    font-family: Arial, sans-serif;
    border-top: 1px solid #00000033;
    padding-top: 10px;
`;

const InfoBlockTitle = styled.span`
    font-family: SF Pro Display;
    font-weight: 700;
    font-size: 16px;
    line-height: 16px;
    letter-spacing: 0;
`

const SubTitleText = styled.span`
    font-family: SF Pro Display;
    font-weight: 600;
    font-size: 14px;
    line-height: 18px;
    letter-spacing: 0;
    color:#000000;
`
const InfoBlockText = styled.div`
    font-family: SF Pro Display;
    font-weight: 600;
    font-size: 14px;
    line-height: 18px;
    letter-spacing: 0;
    color: #00000080;
`
const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const SubTitle = styled.span`
    font-family: SF Pro Display;
    font-weight: 700;
    font-size: 16px;
    line-height: 16px;
    letter-spacing: 0;
`;

const Text = styled.span`
    font-family: SF Pro Display;
    font-weight: 600;
    font-size: 14px;
    line-height: 18px;
    letter-spacing: 0;
    color #000000;
`;

const ContributorCardBox = styled.div` 
    display: flex;
    justify-content: space-between;
    font-family: SF Pro Display;
    font-weight: 600;
    font-size: 13px;
    line-height: 18px;
    letter-spacing: 0;
    vertical-align: middle;
    color :#6777FF;
`;
const InfoGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
  
`;
const InfoBlockContent = styled.div`
    width: 100%;
    display: flex;
    gap: 87px;
`;

const InfoBlock = styled.div`
    display:flex;
    flex-direction: column;
    gap:10px;
`;

const ContributorsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const ContributorCard = styled.div`
    width: 160px;
    min-height: 68px;
    border-radius: 7px;
    border: 1px solid #A4A4A4;
    padding: 4px 6px 9px 9px;
`;

const ContributorsContainer = styled.div`
    display: flex;
    gap: 5px;
`;

const ContributorCardInfo = styled.div`
    padding-top: 7px;
    font-family: SF Pro Display;
    font-weight: 400;
    font-size: 13px;
    line-height: 15px;
    letter-spacing: 0;
    color: #00000080;`

const ArtistsGrid = styled.div`
    width: max-content;
    display: flex;
    gap: 5px;
    margin-top: 10px;
`;
export default PlatformTrackInfo;
